import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Icon } from 'ui-library';
import { ICON_TYPE_CLOSE } from 'ui-library/components/Icon';

import './ReplaceDocumentsConfirmModal.css';

type ReplaceDocumentsConfirmModalProps = {
    documentName?: string;
    languages: string[];
    processes: string[];
    products: string[];
    isLoading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const ReplaceDocumentsConfirmModal = ({
    documentName,
    languages,
    processes,
    products,
    isLoading = false,
    onConfirm,
    onCancel,
}: ReplaceDocumentsConfirmModalProps) => {
    const { t } = useTranslation();

    // parameters the user left empty are not listed
    const parameters = [
        {
            key: 'languages',
            label: t(
                'advisoryDashboard.documents.uploadDocuments.confirmReplace.languages',
                'Languages',
            ),
            values: languages,
        },
        {
            key: 'processes',
            label: t(
                'advisoryDashboard.documents.uploadDocuments.confirmReplace.processes',
                'Processes',
            ),
            values: processes,
        },
        {
            key: 'products',
            label: t(
                'advisoryDashboard.documents.uploadDocuments.confirmReplace.products',
                'Products',
            ),
            values: products,
        },
    ].filter(({ values }) => values.length > 0);

    return (
        <Modal
            visible
            title={t(
                'advisoryDashboard.documents.uploadDocuments.confirmReplace.title',
                {
                    defaultValue: 'You\'re about to update {{documentName}}',
                    documentName: documentName ?? '',
                },
            )}
            okText={t('clientDashboard.confirm', 'Confirm')}
            cancelText={t('clientDashboard.cancel')}
            onOk={onConfirm}
            onCancel={onCancel}
            onClose={onCancel}
            confirmLoading={isLoading}
            cancelButtonProps={{ disabled: isLoading }}
            width={610}
            borderRadius
            closable
            closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
            className="replace-documents-confirm-modal"
        >
            <p className="replace-documents-confirm-modal__subtitle">
                {t(
                    'advisoryDashboard.documents.uploadDocuments.confirmReplace.subtitle',
                    'Are you sure you want to update the document for the following parameters?',
                )}
            </p>

            <ul className="replace-documents-confirm-modal__list">
                {parameters.map(({ key, label, values }) => (
                    <li key={key}>
                        {label}
                        :
                        {' '}
                        {values.join(', ')}
                    </li>
                ))}
            </ul>
        </Modal>
    );
};

export default ReplaceDocumentsConfirmModal;
